import React, { useEffect, useState } from "react";
import { Package, TrendingDown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";

const fmt = (v, cur = "INR") => {
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" }[cur] || (cur + " ");
  return `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function StockSummary() {
  const { active } = useCompany();
  const cur = active?.base_currency || "INR";
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [pickedItem, setPickedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get("/inventory/stock-summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary({ items: [], total_value: 0 }))
      .finally(() => setLoading(false));
  }, [active]);

  useEffect(() => {
    if (!pickedItem) { setLedger([]); return; }
    api.get(`/inventory/ledger?item_id=${pickedItem}`)
      .then((r) => setLedger(r.data.items || []))
      .catch(() => setLedger([]));
  }, [pickedItem]);

  const method = active?.inventory_valuation || "weighted_average";

  return (
    <div className="space-y-4" data-testid="stock-summary-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Inventory</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] flex items-center gap-2">
            <Package size={22} className="text-[#FF5500]"/> Stock Summary
          </h1>
          <div className="text-sm text-[#64748B] mt-1">
            Real-time on-hand quantities and valuation. Method: <b className="font-mono-data">{method}</b>
          </div>
        </div>
        {summary && (
          <div className="card-surface p-3 min-w-[220px]">
            <div className="overline">Total inventory value</div>
            <div className="text-xl sm:text-2xl font-bold font-mono-data text-[#FF5500]" data-testid="stock-total-value">
              {fmt(summary.total_value, cur)}
            </div>
            <div className="text-xs text-[#64748B]">across {summary.count} items</div>
          </div>
        )}
      </div>

      <div className="card-surface p-3 sm:p-4">
        {loading ? <div className="py-6 text-center text-[#64748B]"><Loader2 className="animate-spin inline mr-2" size={14}/>Loading…</div>
         : summary?.items?.length === 0 ? <div className="py-8 text-center text-[#64748B]" data-testid="stock-empty">No stock items yet. Purchase invoices update stock on posting.</div>
         : (<div className="table-scroll">
          <table className="table-clean">
            <thead><tr><th>Item</th><th>SKU</th><th className="text-right">On-hand Qty</th><th className="text-right">Avg Cost</th><th className="text-right">Value</th><th></th></tr></thead>
            <tbody>
              {(summary?.items || []).map((it) => (
                <tr key={it.id} data-testid={`stock-row-${it.id}`}>
                  <td className="font-medium">{it.name}</td>
                  <td className="font-mono-data text-xs text-[#64748B]">{it.sku || "—"}</td>
                  <td className="text-right font-mono-data">{it.on_hand_qty}</td>
                  <td className="text-right font-mono-data">{fmt(it.avg_cost, cur)}</td>
                  <td className="text-right font-mono-data font-semibold">{fmt(it.on_hand_value, cur)}</td>
                  <td className="text-right">
                    <button onClick={() => setPickedItem(pickedItem === it.id ? null : it.id)} data-testid={`stock-drill-${it.id}`} className="text-xs text-[#2563EB] font-semibold">
                      {pickedItem === it.id ? "Hide ledger" : "View ledger"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>)}
      </div>

      {pickedItem && (
        <div className="card-surface p-3 sm:p-4" data-testid="stock-ledger-panel">
          <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
            <TrendingDown size={16} className="text-[#FF5500]"/> Movement ledger · FIFO lot age
          </h3>
          <div className="table-scroll">
            <table className="table-clean">
              <thead><tr><th>Timestamp</th><th>Doc</th><th className="text-right">Qty in</th><th className="text-right">Qty out</th><th className="text-right">Unit cost</th><th className="text-right">Running qty</th><th className="text-right">Running value</th><th className="text-right">COGS</th></tr></thead>
              <tbody>
                {ledger.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-[#64748B]">No movements yet.</td></tr>}
                {ledger.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono-data text-xs">{r.ts ? new Date(r.ts).toLocaleString() : "—"}</td>
                    <td className="font-mono-data text-xs">{r.doc_number}</td>
                    <td className="text-right font-mono-data text-[#16A34A]">{r.qty_in || ""}</td>
                    <td className="text-right font-mono-data text-[#DC2626]">{r.qty_out || ""}</td>
                    <td className="text-right font-mono-data">{fmt(r.unit_cost, cur)}</td>
                    <td className="text-right font-mono-data font-semibold">{r.running_qty}</td>
                    <td className="text-right font-mono-data">{fmt(r.running_value, cur)}</td>
                    <td className="text-right font-mono-data text-[#DC2626]">{r.cogs ? fmt(r.cogs, cur) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
